import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CalculateButton } from "../../components/common/CalculateButton";
import { DownloadButton } from "../../components/common/DownloadButton";
import type { IDisplayField, IField } from "../../interfaces/interfaces";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { useCalculatorMutation } from "../../store/slices/apiSlice";
import { getDataForSystemPlot as pvtDataSelector } from "../../store/slices/pvtCalculatorSlice";
import { getDataForSystemPlot as iprDataSelector } from "../../store/slices/iprCalculatorSlice";
import { getDataForSystemPlot as vlpDataSelector } from "../../store/slices/vlpCalculatorSlice";
import "./SystemPlot.css";
import Chart from "./Chart";
import { OuterData } from "../../components/common/OuterData";
import { getSecondWellIsOpen } from "../../store/slices/appSlice";

type SystemPlotInput = Pick<IField | IDisplayField, "queryName" | "baseValue" | "error">;

const hasErrors = (inputs: SystemPlotInput[]) => inputs.some(input => input.error);

const SystemPlot = () => {
	const [triggerCalculator] = useCalculatorMutation();
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	const pvtData = useAppSelector(pvtDataSelector);
	const iprData1 = useAppSelector(state => iprDataSelector(state, "well1"));
	const iprData2 = useAppSelector(state => iprDataSelector(state, "well2"));
	const vlpData1 = useAppSelector(state => vlpDataSelector(state, "well1"));
	const vlpData2 = useAppSelector(state => vlpDataSelector(state, "well2"));
	const gasRateCriticalInputs = useAppSelector(state => state.gasRateCriticalCalculator.inputs);
	const secondWell = useAppSelector(getSecondWellIsOpen);

	const onCalculate = useCallback(() => {
		const formData = getFormData("well1");
		if (formData)
			triggerCalculator({
				calculator_type: "system_plot",
				well: "well1",
				...formData,
			});

		if (secondWell) {
			const formData2 = getFormData("well2");
			if (formData2)
				triggerCalculator({
					calculator_type: "system_plot",
					well: "well2",
					...formData2,
				});
		}

	}, [dispatch, triggerCalculator, pvtData, iprData1, iprData2, vlpData1, vlpData2, gasRateCriticalInputs]);

	const getFormData = useCallback((wellId: "well1" | "well2") => {
		const allInputs = [
			...pvtData.inputs,
			...(wellId === "well1" ? iprData1.inputs : iprData2.inputs),
			...(wellId === "well1" ? vlpData1.inputs : vlpData2.inputs),
			...gasRateCriticalInputs,
		];

		if (hasErrors(allInputs) ||
			pvtData.errors.length > 0 ||
			iprData1.errors.length > 0 ||
			(secondWell && iprData2.errors.length > 0) ||
			vlpData1.errors.length > 0 ||
			(secondWell && vlpData2.errors.length > 0)) {
			return;
		}

		const formData = allInputs.reduce<Record<string, string | number>>((acc, input) => {
			acc[input.queryName] = input.baseValue ?? 0;
			return acc;
		}, {});

		return formData;
	}, [pvtData, iprData1, iprData2, vlpData1, vlpData2, gasRateCriticalInputs]);

	return (
		<div className="system-plot__container" tabIndex={0}>
			<div>
				<div className="system-plot__controls">
					<CalculateButton onClick={onCalculate} disabled={false} />
					<DownloadButton getFormData={() => getFormData("well1")} calculatorType="system_plot" disabled={false} title={t("download")} />
					{secondWell &&
						<DownloadButton getFormData={() => getFormData("well2")} calculatorType="system_plot" disabled={false} title={t("download")} />
					}
				</div>
				<OuterData
					translationNamespace="pvt"
					inputs={pvtData.inputs}
					errors={pvtData.errors}
				/>
				<OuterData
					translationNamespace="ipr"
					inputs={iprData1.inputs}
					errors={iprData1.errors}
					wellId="well1"
				/>
				{secondWell && (
					<OuterData
						translationNamespace="ipr"
						inputs={iprData2.inputs}
						errors={iprData2.errors}
						wellId="well2"
					/>
				)}
				<OuterData
					translationNamespace="vlp"
					inputs={vlpData1.inputs}
					errors={vlpData1.errors}
				/>
				{
					secondWell && (
						<OuterData
							translationNamespace="vlp"
							inputs={vlpData2.inputs}
							errors={vlpData2.errors}
							wellId="well2"
						/>
					)
				}
			</div>
			<div className="system-plot__charts round-corner-container">
				<Chart wellId="well1" />
				{secondWell && <Chart wellId="well2" />}
			</div>
		</div>
	)
}

export default SystemPlot;